/**
 * The merged product screen's two pure groupings — in their own module so
 * `ProductsPage.tsx` exports only components (react-refresh) and so the test can drive
 * the rules directly: a rule that only exists inside a component is a rule that gets
 * asserted through six clicks.
 *
 * Both are carried over verbatim from the screens the merge retired (roles/products
 * design §2.1): `shelveProducts` from the Product master, `groupHistory` from Catalogue
 * pricing. The claims they encode — a country with no shelves sees a plain flat list;
 * "no current version" is a STATE, distinct from "not priced" and from "priced at
 * 0.00" — are pinned in `products-page.test.tsx`.
 */

import { formatMoney, isMaskedAmount } from '../inquiries/format';
import type { CountryProductDto, ProductDto, ProductGroupDto } from './catalogue-api';

/** Products in this country's own filing order, then Ungrouped last. */
export interface ShelfSection {
  /** `null` is the Ungrouped section — the absence of a shelf, never a group row. */
  group: ProductGroupDto | null;
  products: ProductDto[];
}

/**
 * Bucket the catalogue into its shelves — the ORDER is the country's, not ours.
 *
 * ⚠ A COUNTRY WITH NO GROUPS GETS EXACTLY ONE SECTION WITH A `null` GROUP, rendered as a
 * plain undecorated list: filing is a thing a country opts into, and a country that has
 * not must not find an "Ungrouped" heading over a list that never had one.
 *
 * Groups arrive server-ordered and the order is preserved rather than re-sorted here.
 * Empty shelves are KEPT — a shelf an operator made and has not filled yet is a shelf
 * they are about to fill, and hiding it would make the create look failed.
 */
export function shelveProducts(
  products: readonly ProductDto[],
  groups: readonly ProductGroupDto[],
): ShelfSection[] {
  if (groups.length === 0) {
    return [{ group: null, products: [...products] }];
  }
  const sections: ShelfSection[] = groups.map((group) => ({ group, products: [] }));
  const byId = new Map(sections.map((section) => [section.group?.id, section]));
  const ungrouped: ProductDto[] = [];
  for (const product of products) {
    const section =
      product.product_group_id === null ? undefined : byId.get(product.product_group_id);
    if (section === undefined) {
      // Also the destination for a product filed under a group this screen did not
      // fetch — which should be impossible and is handled anyway, because dropping the
      // row would make a product silently vanish from its own country's catalogue.
      ungrouped.push(product);
    } else {
      section.products.push(product);
    }
  }
  // ALWAYS LAST, and always present once the country has shelves: "not filed yet" is a
  // real state an administrator has to be able to see and act on.
  sections.push({ group: null, products: ungrouped });
  return sections;
}

/**
 * One product's whole price history in ONE LIST — one country, one currency — newest window
 * first.
 *
 * ⚠ PER LIST SINCE MIGRATION 66 (2026-09-22). A country keeps one fixed list per currency it
 * quotes in (Congo: CDF and USD), and the two are independent timelines: a USD price never
 * closes or revises a CDF one. Grouping by product alone made the last row read win
 * (`current` held whichever list came last) and would have described a USD write as closing
 * the CDF price. A product with prices in two currencies now has TWO of these.
 */
export interface ProductHistory {
  productId: string;
  code: string;
  name: string;
  /** Which list this timeline is. */
  currency: string;
  versions: CountryProductDto[];
  current: CountryProductDto | null;
  future: CountryProductDto[];
}

/**
 * Group the flat `/history` rows into per-(product, currency) timelines — where "no current
 * version" becomes a state the screen can render as its own thing.
 */
export function groupHistory(rows: readonly CountryProductDto[]): ProductHistory[] {
  const byList = new Map<string, ProductHistory>();
  for (const row of rows) {
    const key = `${String(row.product_id)}|${row.currency}`;
    let entry = byList.get(key);
    if (entry === undefined) {
      entry = {
        productId: String(row.product_id),
        code: row.product_code,
        name: row.product_name,
        currency: row.currency,
        versions: [],
        current: null,
        future: [],
      };
      byList.set(key, entry);
    }
    entry.versions.push(row);
    if (row.window_state === 'current') entry.current = row;
    if (row.window_state === 'future') entry.future.push(row);
  }
  return [...byList.values()].sort(
    (a, b) => a.code.localeCompare(b.code) || a.currency.localeCompare(b.currency),
  );
}

/** The timelines grouped by product: every list a product has, in currency order. */
export function listsByProduct(histories: readonly ProductHistory[]): Map<string, ProductHistory[]> {
  const out = new Map<string, ProductHistory[]>();
  for (const history of histories) {
    const bucket = out.get(history.productId);
    if (bucket === undefined) out.set(history.productId, [history]);
    else bucket.push(history);
  }
  return out;
}

/**
 * A price row's amount as a person reads it: the money, or — for a list the owner ruled blank
 * (R3) — the words. Never "0.00" for a blank: a blank is not a price of zero.
 */
export function priceText(row: CountryProductDto): string {
  if (row.priced_per_customer) return 'Priced per customer';
  return formatMoney(row.price ?? '', row.currency);
}

/* ─────────────────────────────── the price-change preview (design P2, T-P2) ── */

/**
 * `YYYY-MM-DD` minus one day, in pure calendar arithmetic — `Date.UTC` so no local
 * timezone can slide the day. Mirrors the server's `previousDay` for PREVIEW WORDING
 * only; the server's own copy (`country-products.service.ts`) is the one that writes.
 */
export function previousDayIso(iso: string): string {
  const [y, m, d] = iso.split('-').map((part) => Number(part));
  const date = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, (d ?? 1) - 1));
  return date.toISOString().slice(0, 10);
}

export interface PricePreviewDraft {
  price: string;
  currency: string;
  /** The list's price is left BLANK — priced per customer (R3). `price` is then ignored. */
  perCustomer?: boolean;
  /** `''` means TODAY — resolved by the SERVER in the country's timezone, so the field
   * is deliberately allowed to stay blank rather than prefilled from this browser's
   * clock. The preview says so in words instead. */
  effectiveFrom: string;
}

/**
 * The plain-language preview of what the server's close-incumbent/open-successor
 * procedure will do with this draft (design §2.2) — the humane middle between a bare
 * "current price" field that hides versioning and a raw version table.
 *
 * ⚠ PRESENTATION ONLY, AND SAYS NOTHING THE SERVER WOULD NOT DO. Every rule here is a
 * read-only mirror of `country-products.service.ts` `put`: a same-day write REVISES that
 * version in place; otherwise the incumbent (the version in force the day BEFORE the new
 * start) is closed at `previousDay(from)` and the new version runs until the next queued
 * start caps it. T-P2 pins the sentences against fixtures shaped like the server's own
 * spec cases; the server's specs stay green untouched — the procedure is not this unit's
 * to move.
 *
 * `todayIso` is the CLIENT's day and is used only to word future/past tenses; the blank
 * date's real "today" belongs to the server, and the first sentence says so.
 */
export function previewPriceChange(
  history: ProductHistory | null,
  draft: PricePreviewDraft,
  todayIso: string,
): string[] {
  const perCustomer = draft.perCustomer === true;
  const amount = draft.price.trim();
  if (!perCustomer && (amount === '' || isMaskedAmount(amount))) return [];
  const currency = draft.currency.trim().toUpperCase();
  if (currency === '') return [];

  const from = draft.effectiveFrom.trim();
  const at = from === '' ? todayIso : from;
  // A blank price is named, never shown as money (R3).
  const money = perCustomer
    ? `Priced per customer in ${currency}`
    : formatMoney(amount, currency);
  const sentences: string[] = [];

  if (from === '') {
    sentences.push(
      `${money} applies from today — the server dates it on the country's own calendar, not this browser's clock.`,
    );
  } else if (from > todayIso) {
    sentences.push(`${money} is queued from ${from} — nothing changes before then.`);
  } else if (from < todayIso) {
    sentences.push(
      `${money} is back-dated to ${from}: it rewrites which price applied then. ` +
        'Documents already priced are not re-priced — only the record of what applied changes.',
    );
  } else {
    sentences.push(`${money} applies from today, ${from}.`);
  }

  const versions = history?.versions ?? [];
  const sameDay = versions.find((row) => row.effective_from === at);
  if (sameDay !== undefined) {
    sentences.push(
      `Revises the version already starting ${at} in place — same day means the same version, not a new one.`,
    );
    return sentences;
  }

  // The incumbent: the version in force the day BEFORE the new price starts — the row
  // the server will close. NOT "the latest": a back-dated insert closes the version
  // that covered that date and leaves later ones alone.
  const eve = previousDayIso(at);
  const incumbent = versions.find(
    (row) =>
      row.effective_from <= eve && (row.effective_to === null || row.effective_to >= eve),
  );
  if (incumbent === undefined) {
    sentences.push(
      versions.length === 0
        ? 'First price here — nothing is closed.'
        : `No version covers ${eve}, so nothing is closed — this fills the gap from ${at}.`,
    );
  } else {
    sentences.push(`${priceText(incumbent)} until ${eve} · ${money} from ${at}.`);
  }

  // The successor: an already-queued LATER version caps the new one — the server's
  // `nextStartAfter`, said out loud so a queued rise is never silently shadowed.
  const successor = versions
    .filter((row) => row.effective_from > at)
    .sort((a, b) => a.effective_from.localeCompare(b.effective_from))[0];
  if (successor !== undefined) {
    sentences.push(
      `The queued ${priceText(successor)} still takes over on ${successor.effective_from} — the new price runs until ${previousDayIso(successor.effective_from)}.`,
    );
  }
  return sentences;
}

/* ────────────────────────── the table's filters and print view (owner ruling N-54) ── */

/** What the toolbar narrows the catalogue by. All of it is CLIENT-SIDE over the page the
 * list route already returns (≤ 200 rows per country): the wire, the query keys and the
 * spec's mocks are untouched by a filter, which is what "presentation only" means. */
export interface ProductFilters {
  /** Matched case-insensitively against code AND name; blank matches everything. */
  search: string;
  /** A `ProductKind` value, or `''` for every class. */
  kind: string;
  /** A group id, `'unfiled'` for products on no shelf, or `''` for every shelf. */
  shelf: string;
  /** Retired rows are HIDDEN BY DEFAULT (N-54 item 2); this is the reveal. */
  showRetired: boolean;
}

export const EMPTY_FILTERS: ProductFilters = { search: '', kind: '', shelf: '', showRetired: false };

export function hasActiveFilter(filters: ProductFilters): boolean {
  return filters.search.trim() !== '' || filters.kind !== '' || filters.shelf !== '';
}

/**
 * The rows the table shows, in the order the catalogue files them: shelf by shelf in the
 * country's own order, Ungrouped last — `shelveProducts` flattened — so the "Ungrouped
 * comes LAST" rule survives the move from section headings to a shelf column.
 *
 * Returns the hidden counts beside the rows so the footer can say what it is not
 * showing (WEB-DESIGN-DIRECTION §5.1 rule 8: "N hidden by filters · Clear filters"),
 * split into the two reasons a row is absent — retired-by-default is not a filter
 * the operator set, and the sentence must not blame them for it.
 */
export function filterProducts(
  products: readonly ProductDto[],
  groups: readonly ProductGroupDto[],
  filters: ProductFilters,
): { rows: ProductDto[]; retiredHidden: number; filteredOut: number } {
  const ordered = shelveProducts(products, groups).flatMap((section) => section.products);
  const needle = filters.search.trim().toLowerCase();
  let retiredHidden = 0;
  let filteredOut = 0;
  const rows = ordered.filter((product) => {
    if (!filters.showRetired && !product.active) {
      retiredHidden += 1;
      return false;
    }
    const matchesSearch =
      needle === '' ||
      product.code.toLowerCase().includes(needle) ||
      product.name.toLowerCase().includes(needle);
    const matchesKind = filters.kind === '' || product.kind === filters.kind;
    const matchesShelf =
      filters.shelf === '' ||
      (filters.shelf === 'unfiled'
        ? product.product_group_id === null
        : product.product_group_id === filters.shelf);
    if (!(matchesSearch && matchesKind && matchesShelf)) {
      filteredOut += 1;
      return false;
    }
    return true;
  });
  return { rows, retiredHidden, filteredOut };
}

/** The five HTML-significant characters — the print view interpolates operator text. */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** One printed row — already-rendered strings, so the print view can do no arithmetic
 * and a masked seat prints the marker it sees (`lib/csv.ts`'s rule, applied to paper). */
export interface PrintRow {
  country: string;
  code: string;
  name: string;
  classification: string;
  shelf: string;
  price: string;
  term: string;
  status: string;
}

/**
 * The self-contained document behind the "Export PDF" toolbar button.
 *
 * ⚠ THERE IS NO CATALOGUE PDF ROUTE ON THE SERVER — the only PDF the API renders is a
 * quotation (`/quotes/:id/pdf`), and N-54 forbids an API change. So "export" is the
 * browser's own: this HTML opens in a print window and the operator chooses "Save as
 * PDF" in the print dialog. The button's help text says exactly that. Nothing is
 * fetched, nothing is computed, every cell is the string the table showed — escaped,
 * because names and codes are operator-typed text reaching a document.
 */
export function buildPrintDocument(input: {
  title: string;
  subtitle: string;
  printedOn: string;
  showCountry: boolean;
  rows: readonly PrintRow[];
}): string {
  const head = [
    ...(input.showCountry ? ['Country'] : []),
    'Code',
    'Name',
    'Class',
    'Shelf',
    'Price / from',
    'Term',
    'Status',
  ];
  const body = input.rows
    .map((row) => {
      const cells = [
        ...(input.showCountry ? [row.country] : []),
        row.code,
        row.name,
        row.classification,
        row.shelf,
        row.price,
        row.term,
        row.status,
      ];
      return `<tr>${cells.map((cell) => `<td>${escapeHtml(cell)}</td>`).join('')}</tr>`;
    })
    .join('\n');
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>${escapeHtml(input.title)}</title>
<style>
  @page { size: A4 landscape; margin: 14mm; }
  body { font-family: "Segoe UI", Roboto, Helvetica, Arial, sans-serif; font-size: 10pt; color: #111; margin: 0; }
  h1 { font-size: 14pt; margin: 0 0 2pt; }
  p { margin: 0 0 8pt; color: #444; }
  table { border-collapse: collapse; width: 100%; }
  th, td { text-align: left; padding: 4pt 6pt; border-bottom: 1px solid #ccc; vertical-align: top; }
  th { font-size: 8pt; text-transform: uppercase; letter-spacing: .04em; color: #444; border-bottom: 1px solid #888; }
  td:nth-last-child(3) { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
</style>
</head>
<body>
<h1>${escapeHtml(input.title)}</h1>
<p>${escapeHtml(input.subtitle)} · printed ${escapeHtml(input.printedOn)}</p>
<table>
<thead><tr>${head.map((cell) => `<th>${escapeHtml(cell)}</th>`).join('')}</tr></thead>
<tbody>
${body}
</tbody>
</table>
</body>
</html>
`;
}
